import { League, LeagueParticipant, LeagueStanding, Tournament, TournamentType } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { useState } from 'react';
import { Image, Text, TouchableOpacity, View } from 'react-native';
import { createStyles } from './LeagueDetailScreen.styles';
import { LeaderboardTab } from './tabs/LeaderboardTab';
import { ParticipantsTab } from './tabs/ParticipantsTab';
import { SettingsTab } from './tabs/SettingsTab';
import { TournamentsTab } from './tabs/TournamentsTab';

type TabKey = 'participants' | 'tournaments' | 'leaderboard' | 'settings';

export interface LeagueDetailScreenProps {
  league: League;
  participants: LeagueParticipant[];
  tournaments: Tournament[];
  standings: LeagueStanding[];
  isOwner: boolean;
  // Participant actions
  onAddParticipant: (name: string, userId?: string) => void;
  onRemoveParticipant: (participantId: string) => void;
  // Tournament actions
  onCreateTournament: (name: string, type: TournamentType) => void;
  onTournamentPress: (tournamentId: string) => void;
  creatingTournament: boolean;
  // Settings actions
  onUpdateLeague: (updates: Partial<League>) => void;
  onCompleteLeague: () => void;
  onDeleteLeague: () => void;
  updatingLeague: boolean;
  onUploadLogo: () => void;
  uploadingLogo: boolean;
  // Search user
  onSearchUser: (query: string) => Promise<Array<{ id: string; email: string; name?: string }>>;
  onNavigateToProfile: (userId: string) => void;
  colors: any;
}

export const LeagueDetailScreen = ({
  league,
  participants,
  tournaments,
  standings,
  isOwner,
  onAddParticipant,
  onRemoveParticipant,
  onCreateTournament,
  onTournamentPress,
  creatingTournament,
  onUpdateLeague,
  onCompleteLeague,
  onDeleteLeague,
  updatingLeague,
  onUploadLogo,
  uploadingLogo,
  onSearchUser,
  onNavigateToProfile,
  colors,
}: LeagueDetailScreenProps) => {
  const { t } = useTranslation();
  const styles = createStyles(colors);
  const [activeTab, setActiveTab] = useState<TabKey>('participants');

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'participants', label: t('leagues.participants') },
    { key: 'tournaments', label: t('leagues.tournaments') },
    { key: 'leaderboard', label: t('leagues.leaderboard') },
    ...(isOwner ? [{ key: 'settings' as TabKey, label: t('leagues.settings') }] : []),
  ];

  const getStatusColor = () => {
    switch (league.status) {
      case 'completed':
        return colors.badgeCompleted;
      case 'active':
        return colors.badgeProgress;
      default:
        return colors.badgeSetup;
    }
  };

  const getStatusLabel = () => {
    switch (league.status) {
      case 'setup':
        return t('leagues.setup');
      case 'active':
        return t('leagues.active');
      case 'completed':
        return t('leagues.completed');
      default:
        return league.status;
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.headerContent}>
          <View style={styles.logoCircle}>
            {league.logoUrl ? (
              <Image source={{ uri: league.logoUrl }} style={styles.logoImage} />
            ) : (
              <MaterialCommunityIcons name="trophy-outline" size={28} color={colors.textTertiary} />
            )}
          </View>
          <View style={styles.headerText}>
            <Text style={styles.leagueName}>{league.name}</Text>
            {league.description && <Text style={styles.leagueDesc} numberOfLines={2} ellipsizeMode="tail">{league.description}</Text>}
          </View>
        </View>
        <View style={styles.headerBadges}>
          <View style={[styles.statusBadge, { backgroundColor: getStatusColor() }]}>
            <Text style={styles.statusText}>{getStatusLabel()}</Text>
          </View>
          {league.isPublic && (
            <View style={[styles.publicBadge, { backgroundColor: colors.primary }]}>
              <Text style={styles.statusText}>{t('leagues.public')}</Text>
            </View>
          )}
        </View>
      </View>

      <View style={styles.tabBar}>
        {tabs.map((tab) => (
          <TouchableOpacity
            key={tab.key}
            style={[styles.tab, activeTab === tab.key && styles.tabActive]}
            onPress={() => setActiveTab(tab.key)}
          >
            <Text style={[styles.tabText, activeTab === tab.key && styles.tabTextActive]}>{tab.label}</Text>
          </TouchableOpacity>
        ))}
      </View>

      <View style={styles.tabContent}>
        {activeTab === 'participants' && (
          <ParticipantsTab
            participants={participants}
            leagueStatus={league.status}
            isOwner={isOwner}
            onAddParticipant={onAddParticipant}
            onRemoveParticipant={onRemoveParticipant}
            onSearchUser={onSearchUser}
            onNavigateToProfile={onNavigateToProfile}
            colors={colors}
          />
        )}
        {activeTab === 'tournaments' && (
          <TournamentsTab
            tournaments={tournaments}
            leagueStatus={league.status}
            isOwner={isOwner}
            onCreateTournament={onCreateTournament}
            onTournamentPress={onTournamentPress}
            creatingTournament={creatingTournament}
            colors={colors}
          />
        )}
        {activeTab === 'leaderboard' && (
          <LeaderboardTab
            standings={standings}
            league={league}
            tournaments={tournaments}
            colors={colors}
          />
        )}
        {activeTab === 'settings' && isOwner && (
          <SettingsTab
            league={league}
            tournaments={tournaments}
            isOwner={isOwner}
            onUpdateLeague={onUpdateLeague}
            onCompleteLeague={onCompleteLeague}
            onDeleteLeague={onDeleteLeague}
            updatingLeague={updatingLeague}
            onUploadLogo={onUploadLogo}
            uploadingLogo={uploadingLogo}
            colors={colors}
          />
        )}
      </View>
    </View>
  );
};
